<template>
  <div class="paypal">
    <div v-show="paypalPaymentErrors" class="paypal__payment-errors">
      {{ paypalPaymentErrors }}
    </div>
  </div>
</template>

<script>
import { loadScript } from '@paypal/paypal-js';
import CheckoutPayment from '../../mixins/CheckoutPayment';
import Loader from '../../mixins/Loader';

let paypal = null;

let paypalButtonContainer = null;

export default {
  name: 'Paypal',
  mixins: [CheckoutPayment, Loader],
  props: {
    scriptOptions: {
      type: Object,
      required: true,
    },
    createOrderUrl: {
      type: String,
      required: true,
    },
    captureOrderUrl: {
      type: String,
      required: true,
    },
    errorLogUrl: {
      type: String,
      required: true,
    },
    methodInputSelector: {
      type: String,
      required: true,
    },
    expectedInputValue: {
      type: String,
      required: true,
    },
    formId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      paypalPaymentErrors: null,
    };
  },
  mounted() {
    loadScript(this.scriptOptions).then(instance => {
      paypal = instance;

      this.createButton();
    });
  },
  methods: {
    createButton() {
      const paypalButtonContainerId = 'paypal-button-container';

      paypalButtonContainer = document.getElementById(paypalButtonContainerId);

      if (!window.paypalButtonRendered) {
        paypalButtonContainer = document.createElement('div');
        paypalButtonContainer.id = paypalButtonContainerId;
        paypalButtonContainer.classList.add('form-actions__btn');
        paypalButtonContainer.classList.add('form-actions__btn--paypal');
        paypalButtonContainer.style.display = 'none';
      }

      this.paymentButton.parentNode.appendChild(paypalButtonContainer);

      this.addPaymentMethodChangeListener();

      if (!window.paypalButtonRendered) {
        paypal
          .Buttons({
            style: {
              layout: 'horizontal',
              height: 43,
              label: 'pay',
              tagline: false,
            },

            onInit: (data, actions) => {
              // Disable the button
              actions.disable();

              // Listen for changes to the checkbox
              if (this.termsAndConditionsInput) {
                this.termsAndConditionsInput.addEventListener(
                  'change',
                  event => {
                    if (event.target.checked) {
                      actions.enable();
                    } else {
                      actions.disable();
                    }
                  }
                );
              }

              window.paypalButtonRendered = true;
            },

            onClick: () => {
              const validity = this.reportFormValidity();
              if (validity) {
                this.paypalPaymentErrors = null;
              }
              const form = document.getElementById('payment-form');
              const data = form
                ? JSON.parse(form.dataset.tracking || '{}')
                : {};
              const checkedInput = document.querySelector(
                '.checkout-input__input:checked'
              );
              if (checkedInput) {
                globalThis.dispatchEvent(
                  new CustomEvent('lgf.initiateOrderPayment', {
                    detail: {
                      ecommerce: { ...data, payment_type: checkedInput.value },
                    },
                  })
                );
              }
            },

            createOrder: () => {
              return fetch(this.createOrderUrl, {
                method: 'post',
              })
                .then(res => res.json())
                .then(orderData => {
                  if (undefined === orderData.id) {
                    throw new Error(
                      JSON.stringify({
                        endpoint: 'createOrder',
                        response: orderData,
                      })
                    );
                  }

                  return orderData.id;
                })
                .catch(error => {
                  this.paypalPaymentErrors =
                    'Une erreur s‘est produite, veuillez retenter votre paiement. Si l‘erreur persiste, contactez nous.';

                  this.setLoader(false);
                  this.logError(error);
                });
            },

            onApprove: data => {
              return fetch(
                this.captureOrderUrl.replace('__orderId__', data.orderID),
                {
                  method: 'post',
                }
              )
                .then(res => res.json())
                .then(orderData => {
                  // (1) Recoverable INSTRUMENT_DECLINED -> Show a failure message
                  const errorDetail =
                    Array.isArray(orderData.details) && orderData.details[0];
                  const error = new Error(
                    JSON.stringify({
                      endpoint: 'captureOrder',
                      response: orderData,
                    })
                  );

                  if (
                    errorDetail &&
                    errorDetail.issue === 'INSTRUMENT_DECLINED'
                  ) {
                    this.paypalPaymentErrors =
                      'Le paiement a été refusé. Vérifiez votre mode de paiement et réessayez. Si l‘erreur persiste, contactez nous.';

                    this.setLoader(false);

                    throw error;
                  }

                  // (2) Other non-recoverable errors -> Show a failure message
                  if (errorDetail) {
                    let msg = 'Désolé, votre paiement a échoué.';
                    if (errorDetail.description) {
                      msg += '\n\n' + errorDetail.description;
                    }
                    if (orderData.debug_id) {
                      msg += ' (' + orderData.debug_id + ')';
                    }
                    this.paypalPaymentErrors = msg;

                    this.setLoader(false);

                    throw error;
                  }

                  if (
                    undefined === orderData.id ||
                    orderData.status !== 'COMPLETED'
                  ) {
                    throw error;
                  }

                  // (3) Successful transaction -> hanging-up on checkout workflow
                  this.setLoader();
                  document.getElementById(this.formId).submit();
                })
                .catch(error => {
                  this.paypalPaymentErrors =
                    'Une erreur s‘est produite, veuillez retenter votre paiement. Si l‘erreur persiste, contactez nous.';

                  this.setLoader(false);

                  this.logError(error);
                });
            },
          })
          .render('#' + paypalButtonContainerId);
      }
    },
    addPaymentMethodChangeListener() {
      const elements = Array.from(
        document.querySelectorAll(this.methodInputSelector)
      );

      elements.forEach(input =>
        input.addEventListener('change', event => {
          paypalButtonContainer.style.display =
            this.expectedInputValue === event.target.value
              ? 'inline-flex'
              : 'none';

          this.paymentButton.style.display = this.hasVisibleCustomButtons()
            ? 'none'
            : 'inline-flex';
        })
      );

      elements
        .find(element => element.checked)
        ?.dispatchEvent(new Event('change'));
    },
    logError(error) {
      fetch(this.logErrorUrl, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': '{{ csrf_token() }}',
        },
        body: JSON.stringify({
          error: error instanceof Error ? error.message : error,
        }),
      });
    },
  },
};
</script>
